import type { Sample, SampleStage, SampleStatus } from '@ctcells/contract'

import type { MonitoringDeviceKey } from './monitoringModel.ts'

/**
 * 시료 흐름 — **「이 시료가 지금 어디까지 왔나」를 그리는 순수 모델.**
 *
 * eric 지시(2026-09-21): 「원심분리기가 중요한 게 아니라 **특정 시료가 원심분리가 되었는지**가
 * 중요하고 … 시료의 변화 흐름을 보여 줘.」 그래서 화면의 주인공이 장비에서 시료로 바뀐다.
 *
 * 🔴 **훅도 JSX 도 없다.** 실물 응답·실물 구독 프레임을 그대로 먹여 시험한다.
 *
 * ## 무엇이 서버 값이고 무엇이 아닌가 (2026-09-21 전수 조사)
 *
 * | 축 | 출처 | 실물인가 |
 * | --- | --- | --- |
 * | 시료 목록 · 대조 여부 | `GET /samples` (`sample_type === 'Reference'`) | 🟢 |
 * | 시료의 현재 단계 `stage` | `GET /samples` · 구독 `sample_lifecycle` 로 무효화 | 🟢 |
 * | 시료의 처분 `status` | `GET /samples` — **폐기는 시료마다 갈린다** | 🟢 |
 * | 지금 도는 모듈 | 구독 `batch_progress`+`module_status`(`useRunningDeviceKey`) | 🟢 |
 * | **단계별 성공/실패 판정** | **없다** — `sample_event.error_code` 열은 있는데 쓰는 코드가 0건 | 🔴 |
 *
 * 🔴 **마지막 칸이 이 모델의 설계를 정한다.** 판정은 «주입»받는다(`StageVerdictLookup`) —
 *    모델이 만들지 않는다. 대는 곳은 **`stageVerdicts.ts` 하나**이고, 그것이 읽는 것은
 *    **서버가 준 `error_code`** 뿐이다 — 지금은 전건 `null` 이라 **실패 0건이 정답**이고
 *    값이 오기 시작하면 코드 수정 없이 그대로 선다(`docs/backend-requests.csv` BR-49).
 *    ⚠ **2026-09-22 정정**: 2026-09-21~22 에는 이 자리를 임시 목(`temporaryStageVerdicts.ts`)이
 *      채웠고, 시료 ID 해시로 **서버가 부정하는 실패**를 그렸다. 그 파일은 걷었다 — 되살리지 마라.
 *    ⚠ **판정을 안 넘기면 화면은 「지남 / 대기」까지만 말한다.** 「성공」이라고 적지 않는다 —
 *      단계 표지가 찍힌 것과 그 단계가 성공한 것은 **다른 사실**이다.
 */

/**
 * 단계 축 — 계약 `SampleStage` **5값 그대로, 선형 순서 그대로**.
 *
 * 🔴 **손으로 지은 순서가 아니다.** `app/schemas/generated/orchestrator.py` 의
 *    `SampleReport.stage` `Literal` 이 원본이고, 백엔드도 그 선언 순서를 그대로 캐서
 *    (`app/services/sample_lifecycle.py` `STAGES`) 역행을 거부한다.
 *    `events.asyncapi.yaml:275` `SampleLifecycle.stage` enum 도 같은 다섯이다.
 * ⚠ **계약이 값별 뜻을 적지 않았다.** 실물 시각이 그 비대칭을 보여 준다 —
 *    `centrifuged`·`assembled` 는 그 일이 **끝난** 자리에서 오고(원심 후 3분 뒤 어셈블),
 *    `staining` 은 염색기에 **들어간** 자리에서 오며(어셈블 4초 뒤) `completed` 가 6분 뒤다.
 *    ⇒ 화면이 이 값을 「성공」으로 읽지 않는 까닭이 여기 있다. **「표지가 찍혔다」**까지가 사실이다.
 */
export const FLOW_STAGES: readonly SampleStage[] = [
  'registered',
  'centrifuged',
  'assembled',
  'staining',
  'completed',
]

/**
 * 그 단계를 **하는** 장비. `registered`·`completed` 는 장비 축이 없다(등록은 사람이 하고,
 * 완료는 종착 표지다) — `undefined` 다.
 *
 * 🔴 **이 표가 「진행 중」 판정의 유일한 입력이다.** 구독이 말한 「지금 도는 모듈」과 맞춰 본다.
 *    모듈 상태 어휘(`IDLE` 아님)로 추론하지 않는다 — 그건 프론트가 상태를 만드는 것이다.
 * ⚠ 실물 프레임에서 `completed` 도 `staining-1` 이 말하지만 여기 `stain` 을 적지 않는다.
 *   적으면 염색 단계가 두 칸을 차지해 「완료」가 영원히 진행 중으로 보인다.
 */
const STAGE_DEVICE: Partial<Record<SampleStage, MonitoringDeviceKey>> = {
  centrifuged: 'cent',
  assembled: 'asm',
  staining: 'stain',
}

export function deviceOfStage(stage: SampleStage): MonitoringDeviceKey | undefined {
  return STAGE_DEVICE[stage]
}

/**
 * 장비 → 그 장비가 하는 단계. **위 표를 뒤집은 것이라 표는 여전히 하나다.**
 * 🔴 모르면 `undefined` 다 — 6축 로봇은 단계 축이 없다(시료를 옮길 뿐 바꾸지 않는다).
 */
export function stageOfDevice(device: MonitoringDeviceKey): SampleStage | undefined {
  for (const [stage, key] of Object.entries(STAGE_DEVICE)) {
    if (key === device) return stage as SampleStage
  }
  return undefined
}

/** 단계 줄이 카드로 그리는 셋 — `STAGE_DEVICE` 가 장비를 붙여 준 단계만이다 */
export const DEVICE_STAGES: readonly SampleStage[] = FLOW_STAGES.filter(
  (stage) => STAGE_DEVICE[stage] !== undefined,
)

/**
 * 선형 축에서의 자리. **모르는 값이면 `-1`** — 「아무 단계도 안 지났다」로 본다.
 * 🔴 모르는 값을 0(`registered`)으로 접지 않는다. 접으면 화면이 서버가 안 한 말을 한다.
 */
export function stageIndex(stage: SampleStage | null | undefined): number {
  return stage == null ? -1 : FLOW_STAGES.indexOf(stage)
}

/** 화면이 먹는 시료 한 건. 🔴 `patient_id` 를 **담지 않는다** — 흐름 격자는 PII 를 안 그린다 */
export interface FlowSample {
  sample_id: string
  stage: SampleStage | null
  status: SampleStatus
  /** 대조 시료인가. 판정은 서버 값(`sample_type === 'Reference'`)이다 */
  reference: boolean
  /**
   * 🟢 **폐기 사유 — 서버가 준다.** 계약이 `status=discarded` 일 때 **필수**로 선언한 칸이고
   *    (`Sample.discard_reason`) 실물이 실어 준다(2026-09-21 · 시료 9건 전수 확인).
   *
   * 두 경로에서 온다(`DM-REL-32`) — ⓐ 사람이 내린 폐기 판정(`recover{decision:discard}`)의 사유
   * ⓑ 격리 자동 전이(`SM-BATCH-12` ⓑ)가 적은 **`error_code` 원값**.
   * ⚠ 그래서 **화면이 이 값을 해석하지 않는다** — 사람이 쓴 문장일 수도, 코드일 수도 있다.
   *   그대로 보여 준다.
   */
  discardReason?: string
}

/**
 * `GET /samples` 응답 → 흐름 입력.
 *
 * ⚠ **대조 판정은 서버 값 하나다.** 이름 규칙(`REF-` 접두어)으로 추측하지 않는다 —
 *   실물 대조 시료의 `sample_id` 가 `FE-0920-C3-09` 였고 접두어가 없었다.
 * ⚠ `sample_type` 은 자유 문자열이라 실물에 `Patient`·`Reference` 말고도 들어 있다
 *   (`whole_blood`·`전임상 모델 시료` 실측). **`Reference` 하나만 참으로 본다** — 나머지를
 *   환자 시료로 보는 것이 안전한 쪽이다(대조로 잘못 세면 결과 해석이 틀린다).
 */
export function flowSamples(samples: readonly Sample[]): FlowSample[] {
  return samples.map((sample) => ({
    sample_id: sample.sample_id,
    stage: sample.stage,
    status: sample.status,
    reference: sample.sample_type === 'Reference',
    /* 🔴 키가 없으면 만들지 않는다 — `exclude_none` 이라 폐기가 아닌 행에는 아예 없다 */
    ...(sample.discard_reason == null ? {} : { discardReason: sample.discard_reason }),
  }))
}

/** 그 작업에 속한 시료만. `batch_id` 는 미배정이면 **키가 없다**(BR-23) — `== null` 로 본다 */
export function samplesOfBatch(samples: readonly Sample[], batchId: string | undefined): Sample[] {
  if (batchId === undefined) return []
  return samples.filter((sample) => sample.batch_id != null && sample.batch_id === batchId)
}

/**
 * 격자 한 칸의 상태.
 *
 * | 값 | 서버가 무엇을 말했나 |
 * | --- | --- |
 * | `marked` | 이 시료에 **이 단계 표지가 찍혔다**(`stage` 가 여기 이상) |
 * | `active` | 구독이 **이 단계의 장비가 이 작업을 돌고 있다**고 말한다 |
 * | `pending` | 아직 아무 말도 없다 |
 * | `halted` | 이 시료가 **폐기**돼 여기서 멈췄다 — 뒤 단계는 오지 않는다 |
 *
 * 🔴 **`marked` 와 `active` 는 같이 설 수 있다.** 염색이 그렇다 — 들어갈 때 표지가 찍히고
 *    6분을 더 돈다(실물 시각). 그때 칸은 `active` 로 그려진다(더 「지금」인 사실이 이긴다).
 */
export type FlowCellState = 'marked' | 'active' | 'pending' | 'halted'

/** 단계 판정 — 🔴 **서버가 아직 말하지 않는 축**이다(위 머리말) */
export type StageVerdict = 'pass' | 'fail'

/**
 * 판정을 대는 자리. 🔴 **모델이 만들지 않고 받는다.**
 * `undefined` 를 내면 그 칸은 판정을 말하지 않는다(= 지금의 서버 상태).
 */
export type StageVerdictLookup = (
  sampleId: string,
  stage: SampleStage,
) => { verdict: StageVerdict; errorCode?: string } | undefined

export interface FlowCell {
  stage: SampleStage
  /** **그리는 말.** `marked` 와 `active` 가 겹치면 「지금」이 이긴다 */
  state: FlowCellState
  /**
   * 🔴 **서버가 이 시료에 이 단계 표지를 찍었나** — `state` 와 «다른 축»이다.
   *
   * ⚠ 이 둘을 한 값으로 접었다가 시험이 잡았다(2026-09-21). `state === 'active'` 에는
   *   **두 경우**가 섞인다 — ⓐ 표지가 찍혔고 그 장비가 아직 돈다(염색) ⓑ 아직 안 찍혔는데
   *   그 장비가 돌기 시작했다. 단계 줄이 「9/9 **지남**」을 셀 때 ⓑ 를 세면 **서버가 안 한 말**이
   *   된다. 그리는 말과 센 사실을 갈라 둔다.
   */
  marked: boolean
  /** 🔴 주입받은 판정. 없으면 화면은 「지남」까지만 말한다 */
  verdict?: StageVerdict
  /** 실패 코드 원값(`error_codes.yaml`). 화면은 사전으로 문구를 만든다 */
  errorCode?: string
}

export interface FlowRow {
  sampleId: string
  reference: boolean
  status: SampleStatus
  cells: readonly FlowCell[]
  /** 이 시료에 실패 판정이 몇 칸인가. 판정이 안 들어오면 **언제나 0** 이다 */
  failures: number
}

export interface FlowInput {
  samples: readonly FlowSample[]
  /**
   * 구독이 말한 **지금 도는 모듈**. `undefined` 면 「진행 중」 칸을 **한 칸도 만들지 않는다** —
   * 도는 작업이 없거나, 구독이 끊겨 「지금」을 모르는 것이다(`useRunningDeviceKey` 머리말).
   */
  runningDevice?: MonitoringDeviceKey | undefined
  /** 🔴 안 넘기면 판정 열이 통째로 없다. 그것이 **서버가 주는 만큼만 말하는 화면**이다 */
  verdicts?: StageVerdictLookup | undefined
}

/**
 * 시료 × 단계 격자.
 *
 * 🔴 **시료별 차이를 지어내지 않는다.** 단계는 배치 단위로 오르므로(eric 결정 2026-09-14 ·
 *    `record_batch_wide`) 같은 작업의 시료 9건은 **같은 칸까지** 차는 것이 사실이다. 이 함수는
 *    시료마다 `stage` 를 따로 읽을 뿐이고, 값이 같으면 결과도 같다 — 흔들어 다르게 만들지 않는다.
 * 🟢 **그래도 진짜로 갈리는 축이 하나 있다** — `status === 'discarded'`. 폐기된 시료는
 *    `record_batch_wide` 가 **건너뛰므로**(「버린 시료가 어셈블됐다」가 원장에 남지 않게)
 *    그 시료의 `stage` 가 거기서 멈춘다. 그 차이는 목을 걷어도 남는다.
 */
export function flowRows(input: FlowInput): FlowRow[] {
  const { samples, runningDevice, verdicts } = input

  return samples.map((sample) => {
    const reached = stageIndex(sample.stage)
    const halted = sample.status === 'discarded'
    let failures = 0

    const cells = FLOW_STAGES.map((stage, index): FlowCell => {
      const marked = index <= reached
      const judged =
        marked && verdicts !== undefined ? verdicts(sample.sample_id, stage) : undefined
      if (judged?.verdict === 'fail') failures += 1

      const state: FlowCellState = marked
        ? /* 표지가 찍혔어도 그 장비가 아직 돌고 있으면 「지금」이 이긴다(염색이 그렇다) */
          deviceOfStage(stage) === runningDevice && runningDevice !== undefined && !halted
          ? 'active'
          : 'marked'
        : halted
          ? 'halted'
          : runningDevice !== undefined && deviceOfStage(stage) === runningDevice
            ? 'active'
            : 'pending'

      return {
        stage,
        state,
        marked,
        ...(judged === undefined ? {} : { verdict: judged.verdict }),
        ...(judged?.errorCode === undefined ? {} : { errorCode: judged.errorCode }),
      }
    })

    return {
      sampleId: sample.sample_id,
      reference: sample.reference,
      status: sample.status,
      cells,
      failures,
    }
  })
}

/**
 * **배치 한 건의 단계 줄** — eric 2026-09-23 「현재 진행 상태를 한쪽으로 모는 건 어때?」.
 * 카드마다 있던 5단 스테퍼를 걷고 **구역 머리에 한 벌**만 세울 때 쓰는 값이다.
 *
 * ## 🔴 단계 축은 «배치 단위»다 — 실물로 확인했다 (2026-09-23)
 *
 * 구독 `sample_lifecycle` 프레임 **18건 전수**(실서버 기록 4벌 · 배치 4판)의 payload 키가
 * `{ts, batch_id, stage, module, version}` 이고 **`sample_id` 가 한 건도 없다.**
 * (계약 `events.asyncapi.yaml:274` 는 `sample_id` 를 «선택»으로 선언해 두었지만 서버는 안 싣는다.)
 * 서버가 표지를 **배치 전체에 한 번에** 쓰기 때문이다(`record_batch_wide` · eric 결정 2026-09-14).
 * ⇒ 시료 다섯의 단계를 **다수결로 합치는 것이 아니라**, 배치가 받은 그 한 값을 읽는 것이다.
 *
 * 🟢 **딱 한 축만 시료별로 갈린다 — 폐기(`status === 'discarded'`).** 배치 전체 쓰기가 그 시료를
 *    **건너뛰므로** 거기서 멈춘다. 그래서 「가장 앞선 표지」가 곧 배치의 표지다 —
 *    뒤처질 수는 있어도 **앞설 수는 없다**(서버가 단계 역행을 거부한다).
 * ⚠ 폐기만 남은 배치(전건 폐기)에서는 그 멈춘 자리가 배치의 마지막 표지다 — 그대로 읽는다.
 */
export function batchFlowCells(input: {
  samples: readonly FlowSample[]
  runningDevice?: MonitoringDeviceKey | undefined
}): readonly FlowCell[] {
  const { samples, runningDevice } = input
  const reached = batchStageIndex(samples)

  return FLOW_STAGES.map((stage, index): FlowCell => {
    const marked = index <= reached
    const running = runningDevice !== undefined && deviceOfStage(stage) === runningDevice
    return {
      stage,
      state: marked ? (running ? 'active' : 'marked') : running ? 'active' : 'pending',
      marked,
    }
  })
}

/**
 * 배치가 받은 **가장 앞선 표지**의 자리. 아무 표지도 없으면 `-1`.
 * 🔴 **폐기된 시료는 뒤처질 뿐이라 최대값이 곧 배치의 값이다**(위 머리말).
 */
export function batchStageIndex(samples: readonly FlowSample[]): number {
  return samples.reduce((max, sample) => Math.max(max, stageIndex(sample.stage)), -1)
}

/** 단계 줄 카드 한 장이 말하는 수. **세는 것뿐이고 지어내지 않는다** */
export interface StageTally {
  stage: SampleStage
  device: MonitoringDeviceKey
  /**
   * 이 단계 표지가 **찍힌** 시료 수. 🔴 **`FlowCell.marked` 로 센다** — 그리는 말(`state`)로
   * 세면 「막 돌기 시작했을 뿐 아직 표지가 안 찍힌」 칸까지 「지남」에 들어간다.
   */
  marked: number
  /** 그 작업의 시료 수 */
  total: number
  /** 구독이 「이 장비가 돌고 있다」고 말하는가 */
  running: boolean
  /** 실패 판정 칸 수. 판정이 안 들어오면 0 */
  failures: number
}

/**
 * 단계 줄 — 장비가 붙은 단계 셋(원심 · 어셈블 · 염색).
 *
 * ⚠ **「등록」과 「완료」는 카드가 없다.** 장비 축이 없어서고(`STAGE_DEVICE`), 카드가 곧
 *   그림 탭 선택이기 때문이다 — 갈 곳 없는 카드를 만들지 않는다. 두 단계는 격자의 열로만 선다.
 */
export function stageTallies(
  rows: readonly FlowRow[],
  runningDevice?: MonitoringDeviceKey,
): StageTally[] {
  return DEVICE_STAGES.map((stage) => {
    const device = deviceOfStage(stage)
    /* `DEVICE_STAGES` 가 장비 있는 것만 고른 배열이라 여기서 `undefined` 는 오지 않는다 */
    const cells = rows.map((row) => row.cells.find((cell) => cell.stage === stage))
    return {
      stage,
      device: device ?? 'cent',
      marked: cells.filter((cell) => cell?.marked === true).length,
      total: rows.length,
      running: device !== undefined && device === runningDevice,
      failures: cells.filter((cell) => cell?.verdict === 'fail').length,
    }
  })
}

/**
 * **대조는 맨 뒤** — 한 규칙을 줄 정렬(`sortFlowRows`)과 시료 카드 격자(`SampleBundles`)가 같이 쓴다
 * (eric 2026-09-28 「대조를 맨 밑으로 내리라고 했잖아」). 🔴 **안정 분할**이다 — 대조끼리 · 시료끼리의 차례는
 * 받은 그대로 둔다(서버 차례 · 앞선 정렬).
 */
export function referenceLast<T>(items: readonly T[], isReference: (item: T) => boolean): T[] {
  return [...items.filter((item) => !isReference(item)), ...items.filter(isReference)]
}

/**
 * 격자 정렬 — **시료 ID 오름차순, 대조는 맨 뒤**(챔버 표와 같은 규칙 · README §14-4).
 * 트레이 물리 배치 순서가 아니다 — 우리는 그 순서를 모른다(BR-31).
 */
export function sortFlowRows(rows: readonly FlowRow[]): FlowRow[] {
  return referenceLast(
    [...rows].sort((a, b) => a.sampleId.localeCompare(b.sampleId, 'ko')),
    (row) => row.reference,
  )
}

/**
 * 시료 흐름의 「끊김」 띠를 그릴지 — **끊겼을 때만**이다(eric 2026-09-22 「새로고침할 때마다 떠」).
 * 새로고침 직후 `idle`·`connecting` 은 «아직 안 붙은 것»이지 «끊긴 것»이 아니다 — 그때는 카드가
 * REST 값으로 그대로 서고 띠는 없다. 판정은 둘: ⓐ 붙었다가 닫혔다(`closed`) ⓑ 열려 있는데
 * 무소식 임계를 넘겼다(`open` + stale). 닫힘도 «메시지를 한 번이라도 받은 뒤»만 끊김이다 —
 * 새로고침 직후 토큰 갱신 전에 첫 연결이 닫혔다 다시 붙는 것(실측)은 끊김이 아니다.
 */
export function isFlowStale(stream: {
  status: string
  stale: boolean
  lastMessageAt: number | null
}): boolean {
  if (!stream.stale) return false
  // 열려 있는데 무소식 임계를 넘긴 것 — 임계가 있을 때만 stale 이 선다
  if (stream.status === 'open') return true
  // 닫힘은 «한 번이라도 받은 뒤»만 끊김이다. 새로고침 직후 토큰 갱신 전 첫 시도가 닫히는 것은 끊김이 아니다
  return stream.status === 'closed' && stream.lastMessageAt !== null
}

/*
 * ⚠ **`bundleRows()` 는 2026-09-23 에 지웠다.** 대조 카드가 셋째 열에서 «환자 줄 수만큼»
 *   늘어나던 배치의 계산이었는데, 격자가 두 열이 되고 1행이 **비전 + 대조**로 바뀌면서
 *   세로로 늘어날 칸 자체가 없어졌다(`sampleFlow.css` `.bundles__list` 머리말).
 *   줄 수를 다시 세야 할 것 같으면 그 전에 배치를 다시 읽어라 — 차례가 곧 자리다.
 */
